// Checkout Success Page
// Order confirmation after successful checkout

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DesktopLayout from '@/components/layout/DesktopLayout';
import type { Order, OrderItem } from '@/types';
import styles from './CheckoutSuccessPage.module.css';

export default function CheckoutSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    // Get order from location state
    const stateOrder = location.state as { order?: Order } | null;
    if (stateOrder?.order) {
      setOrder(stateOrder.order);
    } else {
      // If no order in state, redirect to orders list
      navigate('/orders', { replace: true });
    }
  }, [location, navigate]);

  // Format price
  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!order) {
    return (
      <DesktopLayout>
        <div className={styles.successPage}>
          <div className={styles.container}>
            <div className={styles.loading}>載入中...</div>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className={styles.successPage}>
      <div className={styles.container}>
        {/* Success Icon */}
        <div className={styles.successIcon}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="80"
            height="80"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>

        {/* Success Message */}
        <h1 className={styles.title}>訂單建立成功！</h1>
        <p className={styles.message}>
          感謝您的訂購，我們已收到您的訂單。
        </p>

        {/* Order Info */}
        <div className={styles.orderInfo}>
          <div className={styles.infoRow}>
            <span className={styles.label}>訂單編號</span>
            <span className={styles.value}>{order.orderNumber}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.label}>下單時間</span>
            <span className={styles.value}>{formatDate(order.createdAt)}</span>
          </div>
          <div className={styles.infoRow}>
            <span className={styles.label}>訂單金額</span>
            <span className={`${styles.value} ${styles.amount}`}>{formatPrice(order.total)}</span>
          </div>
        </div>

        {/* Order Items */}
        <div className={styles.itemsSection}>
          <h2 className={styles.sectionTitle}>訂單商品</h2>
          <div className={styles.itemsList}>
            {order.items.map((item: OrderItem) => (
              <div key={item.id} className={styles.item}>
                <div className={styles.itemInfo}>
                  <h3 className={styles.itemTitle}>{item.productSnapshot.title}</h3>
                  {item.variantAttributes && (
                    <p className={styles.itemVariant}>
                      {Object.entries(item.variantAttributes)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(', ')}
                    </p>
                  )}
                  <p className={styles.itemMeta}>數量: {item.quantity} × {formatPrice(item.price)}</p>
                </div>
                <p className={styles.itemSubtotal}>{formatPrice(item.subtotal)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Recipient Info */}
        <div className={styles.recipientSection}>
          <h2 className={styles.sectionTitle}>收件人資訊</h2>
          <div className={styles.recipientInfo}>
            <p className={styles.infoLine}>
              <span className={styles.infoLabel}>姓名：</span>
              {order.recipientName}
            </p>
            <p className={styles.infoLine}>
              <span className={styles.infoLabel}>電話：</span>
              {order.recipientPhone}
            </p>
            <p className={styles.infoLine}>
              <span className={styles.infoLabel}>地址：</span>
              {order.recipientAddress}
            </p>
            {order.notes && (
              <p className={styles.infoLine}>
                <span className={styles.infoLabel}>備註：</span>
                {order.notes}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <button
            onClick={() => navigate(`/orders/${order.id}`)}
            className={styles.viewOrderButton}
          >
            查看訂單詳情
          </button>
          <button
            onClick={() => navigate('/orders')}
            className={styles.ordersListButton}
          >
            查看我的訂單
          </button>
          <button
            onClick={() => navigate('/products')}
            className={styles.continueButton}
          >
            繼續購物
          </button>
        </div>
      </div>
      </div>
    </DesktopLayout>
  );
}
